import {
  getDefaultSortBy,
  isFilteredOrSorted,
  removeESpotTiles
} from '@theme/collection-grid-items'

/**
 * E-Spot click tracking for the Intelligems E-Spot test, and removal of
 * E-Spots from a page loaded with a filter or sort in its URL.
 *
 * Pushes { event: 'e_spot_clicked' } onto window.igEvents when a shopper
 * clicks an E-Spot tile (molecule-e-spot-tile.liquid). The Intelligems
 * bundle drains the queue once loaded, so no ig:ready wait is needed. The
 * event must be registered in the Intelligems Events Manager before it can
 * be used as a test metric — see docs/skills/e-spot-test-rollout/SKILL.md.
 *
 * One delegated listener on the document, so tiles added later by infinite
 * scroll or re-rendered by the filters are tracked without re-binding.
 */

export const E_SPOT_CLICK_EVENT = 'e_spot_clicked'
const E_SPOT_LINK_SELECTOR = '[data-e-spot-tile] a'

/**
 * Listen for clicks on E-Spot links and queue an Intelligems event.
 * @param {Document} doc - Document to listen on
 * @param {Window} win - Window that owns the igEvents queue
 * @returns {() => void} - Removes the listener
 */
export function trackESpotClicks(doc = document, win = window) {
  const onClick = (event) => {
    const target = event.target
    if (!(target instanceof Element)) return
    if (!target.closest(E_SPOT_LINK_SELECTOR)) return

    win.igEvents = win.igEvents || []
    win.igEvents.push({ event: E_SPOT_CLICK_EVENT })
  }
  doc.addEventListener('click', onClick)
  return () => doc.removeEventListener('click', onClick)
}

/**
 * A filtered or sorted URL (shared link, reload) is rendered by Liquid with
 * its E-Spots; the merchandised positions mean nothing there, so remove
 * them. Filtering and sorting in the page remove them from the fetched
 * results instead (collection-filters.js, collection-pagination.js).
 * @param {Document} doc
 * @param {string} search - The page's query string
 * @returns {number} - How many tiles were removed
 */
export function removeESpotsIfFiltered(
  doc = document,
  search = window.location.search
) {
  if (!isFilteredOrSorted(search, getDefaultSortBy(doc))) return 0
  const grid = doc.getElementById('collection-products')
  return grid ? removeESpotTiles(grid) : 0
}

removeESpotsIfFiltered()
trackESpotClicks()
